/**
 * Labstep
 */

import { MetadataActionUpdateIsRequired } from 'labstep-web/components/Metadata/Action/UpdateIsRequired';
import { MetadataFormShowEditInputOutput } from 'labstep-web/components/Metadata/Form/ShowEdit/InputOutput';
import { MetadataFormShowEditNumeric } from 'labstep-web/components/Metadata/Form/ShowEdit/Numeric';
import { MetadataFormShowEditOptions } from 'labstep-web/components/Metadata/Form/ShowEdit/Options';
import { MetadataFormShowEditProtocolDeviceContainer as MetadataFormShowEditProtocolDevice } from 'labstep-web/components/Metadata/Form/ShowEdit/ProtocolDevice';
import { MetadataFormShowEditType } from 'labstep-web/components/Metadata/Form/ShowEdit/Type';
import { ProtocolConditionActionToggleIsVariable } from 'labstep-web/components/ProtocolCondition/Action/ToggleIsVariable';
import { ICONS } from 'labstep-web/constants/icons';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { TextWithHelp } from 'labstep-web/core/Text/WithHelp';
import { usePremiumFeatureStatus } from 'labstep-web/hooks/premium-feature';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Group } from 'labstep-web/models/group.model';
import type { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import type React from 'react';
import type { RowsType } from '../../types';

export const getRows = (
  entity: Protocol | Experiment,
  metadataEntity: Metadata,
  groupId: Group['id'],
  onSuccess?: (response: any) => void,
): RowsType => {
  const rows: RowsType = [
    {
      name: 'input-output',
      header: (
        <TextWithHelp text="Input / Output" icon="exchange" header />
      ),
      content: (metadata): React.ReactElement => (
        <MetadataFormShowEditInputOutput metadata={metadata} />
      ),
      dataTestId: 'metadata-input-output',
    },
    {
      name: 'data-type',
      header: (
        <TextWithHelp
          text="Data Type"
          icon={ICONS.metadata.primary}
          header
        />
      ),
      content: (metadata): React.ReactElement => (
        <MetadataFormShowEditType metadata={metadata} />
      ),
      dataTestId: 'metadata-type',
    },
    {
      name: 'required',
      header: (
        <TextWithHelp text="Required" icon="exclamation" header />
      ),
      content: (metadata): React.ReactElement => (
        <MetadataActionUpdateIsRequired metadata={metadata} />
      ),
      dataTestId: 'metadata-required',
    },
  ];
  rows.splice(2, 0, {
    name: 'variable',
    header: (
      <TextWithHelp
        text="Variable"
        icon={ICONS.protocol_condition.info.variable}
        helperText="Enable to assign this field different values across different conditions."
        header
      />
    ),
    content: (metadata): React.ReactElement => (
      <ProtocolConditionActionToggleIsVariable
        entity={metadata}
        protocol={entity}
        params={{ onSuccess }}
      />
    ),
    dataTestId: 'metadata-is-variable',
  });
  if (metadataEntity.type === MetadataType.options) {
    rows.splice(2, 0, {
      name: 'options',
      header: <TextWithHelp text="Options" icon="list" header />,
      content: (metadata): React.ReactElement => (
        <MetadataFormShowEditOptions metadata={metadata} />
      ),
      dataTestId: 'metadata-options',
    });
  }
  if (metadataEntity.canBeLinkedToDevice) {
    rows.push({
      name: 'device',
      header: (
        <TextWithHelp
          text="Device"
          icon={ICONS.device.primary}
          header
        />
      ),
      content: (metadata): React.ReactElement => (
        <ParamsStateContextProvider value={{}}>
          <MetadataFormShowEditProtocolDevice
            metadata={metadata}
            parent={entity}
            params={{ group_id: groupId }}
          />
        </ParamsStateContextProvider>
      ),
      dataTestId: 'metadata-device',
    });
  }
  if (
    metadataEntity.type === MetadataType.numeric &&
    metadataEntity.is_variable
  ) {
    rows.push({
      name: 'unit',
      header: (
        <TextWithHelp
          text="Unit"
          icon={ICONS.protocol_value.info.unit}
          header
        />
      ),
      content: (metadata): React.ReactElement => (
        <MetadataFormShowEditNumeric
          metadata={metadata}
          type="unit"
        />
      ),
      dataTestId: 'metadata-unit',
    });
  }

  const premiumRows = usePremiumFeatureStatus(
    rows,
    'name',
    'metadata_detail_table',
  );

  return premiumRows;
};
